import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiX } from 'react-icons/fi';
import {
  Pipeline,
  CreateFundInput,
  FUND_LOGO_COLORS,
} from '../../types/fundraise';

// Type-safe icon wrapper
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

interface AddFundModalProps {
  pipeline: Pipeline;
  onClose: () => void;
  onSubmit: (input: CreateFundInput) => Promise<void>;
}

const AddFundModal: React.FC<AddFundModalProps> = ({
  pipeline,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [stage, setStage] = useState(pipeline.stages[0]?.id || '');
  const [logoInitials, setLogoInitials] = useState('');
  const [logoColor, setLogoColor] = useState('purple');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Fund name is required');
      return;
    }

    if (!stage) {
      setError('Please select a stage');
      return;
    }

    // Validate numeric inputs for NaN
    const minAmountNum = minAmount ? parseInt(minAmount, 10) : null;
    const maxAmountNum = maxAmount ? parseInt(maxAmount, 10) : null;

    if (minAmount && (minAmountNum === null || isNaN(minAmountNum))) {
      setError('Min amount must be a valid number');
      return;
    }

    if (maxAmount && (maxAmountNum === null || isNaN(maxAmountNum))) {
      setError('Max amount must be a valid number');
      return;
    }

    setIsSubmitting(true);

    try {
      const input: CreateFundInput = {
        pipeline_id: pipeline.id,
        name: name.trim(),
        min_amount: minAmountNum,
        max_amount: maxAmountNum,
        stage,
        logo_initials: logoInitials || undefined,
        logo_color: logoColor,
        website_url: websiteUrl || undefined,
        contact_name: contactName || undefined,
        contact_email: contactEmail || undefined,
        notes: notes || undefined,
      };

      await onSubmit(input);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to add fund');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div className="su-fundraise-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-fundraise-modal" role="dialog" aria-modal="true" aria-labelledby="add-fund-title">
        <div className="su-fundraise-modal-header">
          <div>
            <h2 id="add-fund-title" className="su-fundraise-modal-title">Add Fund</h2>
            <p className="su-fundraise-modal-subtitle">
              Add a new investor/fund to {pipeline.name}
            </p>
          </div>
          <button className="su-fundraise-modal-close" onClick={onClose} aria-label="Close modal">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="su-fundraise-modal-body">
            {error && (
              <div className="su-fundraise-error-message">
                {error}
              </div>
            )}

            <div className="su-fundraise-form-group">
              <label htmlFor="fund-name" className="su-fundraise-label">Fund Name *</label>
              <input
                id="fund-name"
                type="text"
                className="su-fundraise-input"
                placeholder="e.g., Sequoia Capital"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="su-fundraise-form-row">
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="fund-min-amount" className="su-fundraise-label">Min Amount ($)</label>
                <input
                  id="fund-min-amount"
                  type="number"
                  min="0"
                  className="su-fundraise-input"
                  placeholder="e.g., 500000"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                />
              </div>
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="fund-max-amount" className="su-fundraise-label">Max Amount ($)</label>
                <input
                  id="fund-max-amount"
                  type="number"
                  min="0"
                  className="su-fundraise-input"
                  placeholder="e.g., 5000000"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="su-fundraise-form-group">
              <label htmlFor="fund-stage" className="su-fundraise-label">Stage *</label>
              <select
                id="fund-stage"
                className="su-fundraise-input"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
              >
                {pipeline.stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="su-fundraise-form-row">
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="fund-logo-initials" className="su-fundraise-label">Logo Initials</label>
                <input
                  id="fund-logo-initials"
                  type="text"
                  className="su-fundraise-input"
                  placeholder="e.g., SC"
                  maxLength={2}
                  value={logoInitials}
                  onChange={(e) => setLogoInitials(e.target.value.toUpperCase())}
                />
                <p className="su-fundraise-helper">
                  Leave blank to auto-generate from name
                </p>
              </div>
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label className="su-fundraise-label">Logo Color</label>
                <div className="su-color-picker">
                  {FUND_LOGO_COLORS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`su-color-option ${logoColor === c.id ? 'selected' : ''}`}
                      style={{ backgroundColor: c.bg }}
                      onClick={() => setLogoColor(c.id)}
                      title={c.id}
                      aria-label={`Select ${c.id} color`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="su-fundraise-form-group">
              <label htmlFor="fund-website" className="su-fundraise-label">Website URL</label>
              <input
                id="fund-website"
                type="url"
                className="su-fundraise-input"
                placeholder="https://..."
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
              />
            </div>

            <div className="su-fundraise-form-row">
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="fund-contact-name" className="su-fundraise-label">Contact Name</label>
                <input
                  id="fund-contact-name"
                  type="text"
                  className="su-fundraise-input"
                  placeholder="e.g., John Smith"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </div>
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="fund-contact-email" className="su-fundraise-label">Contact Email</label>
                <input
                  id="fund-contact-email"
                  type="email"
                  className="su-fundraise-input"
                  placeholder="john@example.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="su-fundraise-form-group">
              <label htmlFor="fund-notes" className="su-fundraise-label">Notes</label>
              <textarea
                id="fund-notes"
                className="su-fundraise-textarea"
                placeholder="Any additional notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="su-fundraise-modal-footer">
            <button
              type="button"
              className="su-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="su-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Fund'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default AddFundModal;
